import { auth } from "../firebase";
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  query,
  where,
  writeBatch,
  Timestamp
} from "firebase/firestore";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import Layout from "../components/Layout";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { updateMasterMetadata } from "../utils/cacheManager";

import Papa from "papaparse";
import * as XLSX from "xlsx";
import { syncPartyMasterOpeningBalance, getISTDateString } from "../services/cashierService";

function PartyManager() {
  const [parties, setParties] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [officialClientId, setOfficialClientId] = useState("");

  // Fetch Official Client ID
  const fetchClientId = async () => {
    try {
      const snap = await getDoc(doc(db, "companyInfo", "default"));
      if (snap.exists()) {
        const cid = snap.data().clientId || "";
        setOfficialClientId(cid);
        setNewParty(p => ({ ...p, clientId: cid }));
      }
    } catch (err) {
      console.error("Error fetching clientId:", err);
    }
  };

  // ---------- CREATE PARTY FORM ----------
  const [newParty, setNewParty] = useState({
    partyId: "",
    name: "",
    address: "",
    route: "",
    distance: "",
    gstno: "",
    phone: "",
    clientId: "",
    vehicleno: "",
    dms_name: "",
    easycig_name: "",
    partytype: "hawker",
    creditLimit: "",
    opening_balance: "",
    payment_mode: "Cash", // Default to Cash
    routePotential: "",
    depositAmount: "",
  });

  // ---------- BULK UPLOAD STATE ----------
  const [csvPreview, setCsvPreview] = useState([]);

  // Load parties once
  useEffect(() => {
    loadParties();
    fetchClientId();
  }, []);

  const loadParties = async () => {
    const snap = await getDocs(collection(db, "partyMaster"));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setParties(list);
    setFiltered(list);
  };

  const onChangeNewParty = (field, value) => {
    setNewParty((prev) => ({ ...prev, [field]: value }));
  };

  // ============= TAB: CREATE PARTY =============
  const handleCreateParty = async (e) => {
    e.preventDefault();

    if (!newParty.partyId || !newParty.name) {
      alert("Party ID and Name are required.");
      return;
    }

    const id = newParty.partyId.trim();

    const payload = {
      partyId: id,
      name: newParty.name || "",
      address: newParty.address || "",
      route: newParty.route || "",
      distance: newParty.distance || "",
      gstno: newParty.gstno || "",
      phone: newParty.phone || "",
      clientId: officialClientId,
      vehicleno: newParty.vehicleno || "",
      dms_name: newParty.dms_name || "",
      easycig_name: newParty.easycig_name || "",
      partytype: newParty.partytype || "",
      creditLimit: newParty.creditLimit || "",
      opening_balance: newParty.opening_balance || "",
      payment_mode: newParty.payment_mode || "Cash",
      routePotential: Number(newParty.routePotential || 0),
      depositAmount: Number(newParty.depositAmount || 0),
    };

    try {
      setUploading(true);
      let photoUrl = newParty.photoUrl || "";

      if (selectedFile) {
        const fileRef = ref(storage, `party_photos/${id}`);
        await uploadBytes(fileRef, selectedFile);
        photoUrl = await getDownloadURL(fileRef);
      }

      await setDoc(doc(db, "partyMaster", id), { ...payload, photoUrl });
      alert("Party created/updated successfully!");

      // Clear form
      setNewParty({
        partyId: "",
        name: "",
        address: "",
        route: "",
        distance: "",
        gstno: "",
        phone: "",
        clientId: officialClientId,
        vehicleno: "",
        dms_name: "",
        easycig_name: "",
        partytype: "hawker",
        creditLimit: "",
        opening_balance: "",
        payment_mode: "Cash",
        routePotential: "",
        depositAmount: "",
      });
      setSelectedFile(null);
      loadParties();
      await updateMasterMetadata(db, "party");
    } catch (err) {
      console.error(err);
      alert("Error saving party");
    } finally {
      setUploading(false);
    }
  };

  // ============= TAB: EDIT PARTIES =============

  // Search
  const handleSearch = (text) => {
    const s = text.toLowerCase();
    const results = parties.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(s) ||
        (p.partyId || "").toLowerCase().includes(s)
    );
    setFiltered(results);
  };

  // Only phone & vehicleno editable
  const updateFieldLocal = (id, field, value) => {
    setFiltered((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const savePartyChanges = async (party) => {
    try {
      await updateDoc(doc(db, "partyMaster", party.id), {
        phone: party.phone || "",
        vehicleno: party.vehicleno || "",
        payment_mode: party.payment_mode || "Cash",
        partytype: party.partytype || "hawker",
        clientId: officialClientId,
        routePotential: Number(party.routePotential || 0),
        depositAmount: Number(party.depositAmount || 0),
      });
      alert("Updated successfully!");
	  await updateMasterMetadata(db, "party");
      loadParties();
    } catch (err) {
      console.error(err);
      alert("Error updating party");
    }
  };

  const deleteParty = async (party) => {
    const ok = window.confirm(
      `Are you sure you want to DELETE party "${party.name}" (${party.partyId})?`
    );
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "partyMaster", party.id));
      alert("Party deleted.");

      const updated = filtered.filter((p) => p.id !== party.id);
	  await updateMasterMetadata(db, "party");
      setFiltered(updated);
      setParties(updated);
    } catch (err) {
      console.error(err);
      alert("Error deleting party");
    }
  };

  // ============= TAB: BULK UPLOAD =============

  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        console.log("CSV parsed:", result.data);
        setCsvPreview(result.data);
      },
    });
  };

  const uploadCsvToFirestore = async () => {
    if (csvPreview.length === 0) {
      alert("No CSV data to upload.");
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const row of csvPreview) {
      try {
        const id = (row.partyId || row.PartyID || "").trim();
        if (!id) {
          failCount++;
          continue;
        }

        const payload = {
          partyId: id,
          name: row.name || row.PARTYNAME || "",
          address: row.address || "",
          route: row.route || "",
          distance: row.distance || "",
          gstno: row.gstno || "",
          phone: row.phone || "",
          clientId: officialClientId,
          vehicleno: row.vehicleno || "",
          dms_name: row.dms_name || "",
          easycig_name: row.easycig_name || "",
          partytype: row.partytype || "",
          creditLimit: row.creditLimit || "",
          opening_balance: row.opening_balance || "",
          payment_mode: row.payment_mode || row.PAYMENT_MODE || "Cash",
          routePotential: Number(row.routePotential || row.ROUTEPOTENTIAL || 0),
          depositAmount: Number(row.depositAmount || row.DEPOSIT_AMOUNT || 0),
        };

        await setDoc(doc(db, "partyMaster", id), payload, { merge: true });
        successCount++;
      } catch (err) {
        console.error("Error on row:", row, err);
        failCount++;
      }
    }

    alert(
      `Bulk upload complete.\nSuccess: ${successCount}\nFailed: ${failCount}`
    );
	await updateMasterMetadata(db, "party");
    loadParties();
    setShowBulk(false);
  };

  // ============= SYNC ALL BALANCES =============
  const handleSyncAllBalances = async () => {
    const ok = window.confirm("Sync all opening balances from Today's ledger? This will overwrite Master records.");
    if (!ok) return;

    setUploading(true);
    try {
      const today = getISTDateString();
      // Fetch today's ledger records
      const q = query(collection(db, "salesman_ledger"), where("dateIST", "==", today));
      const snap = await getDocs(q);
      
      let count = 0;
      for (const d of snap.docs) {
        const data = d.data();
        if (data.partyId) {
          await syncPartyMasterOpeningBalance(data.partyId, data.openingBalance);
          count++;
        }
      }
      alert(`Sync complete! Updated ${count} parties.`);
      loadParties();
    } catch (err) {
      console.error(err);
      alert("Sync failed");
    } finally {
      setUploading(false);
    }
  };

  // ============= EXPORT TO EXCEL =============
  const handleExportExcel = () => {
    if (parties.length === 0) {
      alert("No data to export.");
      return;
    }

    const dataToExport = parties.map(p => ({
      "Party ID": p.partyId || "",
      "Name": p.name || "",
      "Address": p.address || "",
      "Route": p.route || "",
      "Distance": p.distance || "",
      "GST No": p.gstno || "",
      "Phone": p.phone || "",
      "Client ID": officialClientId,
      "Vehicle No": p.vehicleno || "",
      "DMS Name": p.dms_name || "",
      "EasyCig Name": p.easycig_name || "",
      "Party Type": p.partytype || "",
      "Credit Limit": p.creditLimit || "",
      "Opening Balance": p.opening_balance || "",
      "Payment Mode": p.payment_mode || "Cash",
      "Route Potential": p.routePotential || 0,
      "Deposit Amount": p.depositAmount || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PartyMaster");
    XLSX.writeFile(workbook, `PartyMaster_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleDownloadTemplate = () => {
    const csvContent = "partyId,name,address,route,distance,gstno,phone,clientId,vehicleno,dms_name,easycig_name,partytype,creditLimit,opening_balance,payment_mode,routePotential,depositAmount\n" + 
      `TEST001,Testing Party,Bengaluru,Route 1,10km,,9999999999,${officialClientId},KA01AB1234,DMS-TEST,EC-TEST,hawker,200000,0,Cash,0,0`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "party_master_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const editPartyInForm = (p) => {
    setNewParty({
      partyId: p.partyId || "",
      name: p.name || "",
      address: p.address || "",
      route: p.route || "",
      distance: p.distance || "",
      gstno: p.gstno || "",
      phone: p.phone || "",
      clientId: officialClientId,
      vehicleno: p.vehicleno || "",
      dms_name: p.dms_name || "",
      easycig_name: p.easycig_name || "",
      partytype: p.partytype || "",
      creditLimit: p.creditLimit || "",
      opening_balance: p.opening_balance || "",
      payment_mode: p.payment_mode || "Cash",
      routePotential: p.routePotential || "",
      depositAmount: p.depositAmount || "",
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ============= RENDER UI =============

  return (
    <Layout>
      <div style={{ padding: 20, width: "100%", boxSizing: "border-box" }}>
        
        {/* Header and Top Action Buttons */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0 }}>Party Management</h2>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => setShowBulk(!showBulk)}
              style={{
                backgroundColor: showBulk ? "#6c757d" : "#007bff",
                color: "white",
                padding: "8px 16px",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: "bold"
              }}
            >
              Bulk Upload Parties
            </button>
            <button
              onClick={handleExportExcel}
              style={{
                backgroundColor: "#28a745",
                color: "white",
                padding: "8px 16px",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: "bold"
              }}
            >
              Export to Excel
            </button>
            <button
              onClick={handleSyncAllBalances}
              disabled={uploading}
              style={{
                backgroundColor: "#17a2b8",
                color: "white",
                padding: "8px 16px",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                fontWeight: "bold"
              }}
            >
              Sync Balances
            </button>
          </div>
        </div>

        {/* Bulk Upload Section */}
        {showBulk && (
          <div style={{ background: "#f8f9fa", padding: 20, borderRadius: 8, border: "1px solid #ddd", marginBottom: 30 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 15 }}>
              <h3 style={{ margin: 0 }}>Bulk Upload Parties (CSV)</h3>
              <button
                onClick={handleDownloadTemplate}
                style={{
                  backgroundColor: "#17a2b8",
                  color: "white",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Download Template
              </button>
            </div>
            
            <p style={{ fontSize: "14px", color: "#666" }}>
              Expected columns in CSV: partyId, name, address, route, distance,
              gstno, phone, clientId, vehicleno, dms_name, easycig_name,
              partytype, creditLimit, opening_balance, payment_mode, routePotential, depositAmount
            </p>

            <input type="file" accept=".csv" onChange={handleCsvFile} style={{ marginBottom: 20 }} />

            {csvPreview.length > 0 && (
              <>
                <h4>Preview ({csvPreview.length} rows)</h4>
                <div style={{ maxHeight: "300px", overflow: "auto", border: "1px solid #eee", background: "#fff" }}>
                  <table border="1" cellPadding="4" style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                    <thead style={{ background: "#eee", position: "sticky", top: 0 }}>
                      <tr>
                        {Object.keys(csvPreview[0]).map((h) => (
                          <th key={h} style={{ padding: 8 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.map((row, idx) => (
                        <tr key={idx}>
                          {Object.keys(csvPreview[0]).map((h) => (
                            <td key={h} style={{ padding: 8 }}>{row[h]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button
                  style={{ 
                    marginTop: 15, 
                    backgroundColor: "#007bff", 
                    color: "white", 
                    padding: "10px 20px", 
                    border: "none", 
                    borderRadius: 4, 
                    cursor: "pointer",
                    fontWeight: "bold"
                  }}
                  onClick={uploadCsvToFirestore}
                >
                  Upload to Firestore
                </button>
              </>
            )}
          </div>
        )}

        {/* Create / Edit Form Section */}
        <div style={{ 
          background: "white", 
          padding: 25, 
          borderRadius: 10, 
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)", 
          marginBottom: 40,
          border: "1px solid #eee"
        }}>
          <h3 style={{ marginTop: 0, marginBottom: 20, borderBottom: "2px solid #007bff", display: "inline-block", paddingBottom: 5 }}>
            Create New Party
          </h3>
          
          <form onSubmit={handleCreateParty}>
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", 
              gap: "20px",
              marginBottom: 20 
            }}>
              
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Party ID:</label>
                <input
                  type="text"
                  value={newParty.partyId}
                  onChange={(e) => onChangeNewParty("partyId", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                  required
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Name:</label>
                <input
                  type="text"
                  value={newParty.name}
                  onChange={(e) => onChangeNewParty("name", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                  required
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Address:</label>
                <input
                  type="text"
                  value={newParty.address}
                  onChange={(e) => onChangeNewParty("address", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Route:</label>
                <input
                  type="text"
                  value={newParty.route}
                  onChange={(e) => onChangeNewParty("route", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Distance:</label>
                <input
                  type="text"
                  value={newParty.distance}
                  onChange={(e) => onChangeNewParty("distance", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>GST No:</label>
                <input
                  type="text"
                  value={newParty.gstno}
                  onChange={(e) => onChangeNewParty("gstno", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Phone:</label>
                <input
                  type="text"
                  value={newParty.phone}
                  onChange={(e) => onChangeNewParty("phone", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Client ID:</label>
                <div style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc", background: "#f8f9fa", fontWeight: "bold", color: "#007bff" }}>
                  {officialClientId} <small style={{ color: "#666", fontWeight: "normal" }}>(AUTO)</small>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Vehicle No:</label>
                <input
                  type="text"
                  value={newParty.vehicleno}
                  onChange={(e) => onChangeNewParty("vehicleno", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>DMS Name:</label>
                <input
                  type="text"
                  value={newParty.dms_name}
                  onChange={(e) => onChangeNewParty("dms_name", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>EasyCig Name:</label>
                <input
                  type="text"
                  value={newParty.easycig_name}
                  onChange={(e) => onChangeNewParty("easycig_name", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Party Type:</label>
                <select
                  value={newParty.partytype}
                  onChange={(e) => onChangeNewParty("partytype", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                >
                  <option value="hawker">hawker</option>
                  <option value="wholesale">wholesale</option>
                  <option value="swd">swd</option>
                  <option value="npsr">npsr</option>
				  <option value="management">management</option>
                </select>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Credit Limit:</label>
                <input
                  type="text"
                  value={newParty.creditLimit}
                  onChange={(e) => onChangeNewParty("creditLimit", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Opening Balance:</label>
                <input
                  type="text"
                  value={newParty.opening_balance}
                  onChange={(e) => onChangeNewParty("opening_balance", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Cash/Credit:</label>
                <select
                  value={newParty.payment_mode}
                  onChange={(e) => onChangeNewParty("payment_mode", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                >
                  <option value="Cash">Cash</option>
                  <option value="Credit">Credit</option>
                </select>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Avg Route Potential (Min Qty):</label>
                <input
                  type="number"
                  value={newParty.routePotential}
                  onChange={(e) => onChangeNewParty("routePotential", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontWeight: "bold", fontSize: "14px" }}>Deposit Amount:</label>
                <input
                  type="number"
                  value={newParty.depositAmount}
                  onChange={(e) => onChangeNewParty("depositAmount", e.target.value)}
                  style={{ padding: "10px", borderRadius: 4, border: "1px solid #ccc" }}
                />
              </div>

            </div>

            <div style={{ marginBottom: 15 }}>
            <label><b>Party Photo:</b></label><br />
            <input 
              type="file" 
              accept="image/*"
              onChange={e => setSelectedFile(e.target.files[0])}
              style={{ marginTop: 5 }}
            />
            {newParty.photoUrl && <p style={{ fontSize: 11, color: "blue" }}>Existing photo: {newParty.photoUrl}</p>}
          </div>

          <button
            type="submit"
            disabled={uploading}
            style={{
              padding: "12px 24px",
              backgroundColor: uploading ? "#aaa" : "#28a745",
              color: "white",
              border: "none",
              borderRadius: 4,
              cursor: uploading ? "not-allowed" : "pointer",
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            {uploading ? "Uploading & Saving..." : "Save Party"}
          </button>
          </form>
        </div>

        {/* Existing Parties Table Section */}
        <div style={{ background: "white", padding: 25, borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.1)", border: "1px solid #eee" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ margin: 0 }}>Edit / Delete Parties</h3>
            <input
              type="text"
              placeholder="Search by party name or ID..."
              onChange={(e) => handleSearch(e.target.value)}
              style={{
                padding: "10px",
                width: "350px",
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: "14px"
              }}
            />
          </div>

          <div style={{ maxHeight: "60vh", overflow: "auto" }}>
            <table border="1" cellPadding="6" style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead style={{ background: "#f1f3f5", position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ padding: 12 }}>Party ID</th>
                  <th style={{ padding: 12 }}>Name</th>
                  <th style={{ padding: 12 }}>Address</th>
                  <th style={{ padding: 12 }}>Route</th>
                  <th style={{ padding: 12 }}>Distance</th>
                  <th style={{ padding: 12 }}>GST No</th>
                  <th style={{ padding: 12 }}>Phone</th>
                  <th style={{ padding: 12 }}>Client ID</th>
                  <th style={{ padding: 12 }}>Vehicle No</th>
                  <th style={{ padding: 12 }}>DMS Name</th>
                  <th style={{ padding: 12 }}>EasyCig Name</th>
                  <th style={{ padding: 12 }}>Party Type</th>
                  <th style={{ padding: 12 }}>Credit Limit</th>
                  <th style={{ padding: 12 }}>Opening Balance</th>
                  <th style={{ padding: 12 }}>Payment Mode</th>
                  <th style={{ padding: 12 }}>Route Potential</th>
                  <th style={{ padding: 12 }}>Deposit</th>
                  <th style={{ padding: 12 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ padding: 8, fontWeight: "bold" }}>{p.partyId}</td>
                    <td style={{ padding: 8 }}>{p.name}</td>
                    <td style={{ padding: 8 }}>{p.address}</td>
                    <td style={{ padding: 8 }}>{p.route}</td>
                    <td style={{ padding: 8 }}>{p.distance}</td>
                    <td style={{ padding: 8 }}>{p.gstno}</td>

                    <td style={{ padding: 8 }}>
                      <input
                        type="text"
                        value={p.phone || ""}
                        onChange={(e) => updateFieldLocal(p.id, "phone", e.target.value)}
                        style={{ width: "90px", padding: 4 }}
                      />
                    </td>

                    <td style={{ padding: 8 }}>
                      <span style={{ fontWeight: "bold", color: "#007bff" }}>{officialClientId}</span>
                    </td>

                    <td style={{ padding: 8 }}>
                      <input
                        type="text"
                        value={p.vehicleno || ""}
                        onChange={(e) => updateFieldLocal(p.id, "vehicleno", e.target.value)}
                        style={{ width: "95px", padding: 4 }}
                      />
                    </td>

                    <td style={{ padding: 8 }}>{p.dms_name}</td>
                    <td style={{ padding: 8 }}>{p.easycig_name}</td>
                    <td style={{ padding: 8 }}>
                      <select
                        value={p.partytype || "hawker"}
                        onChange={(e) => updateFieldLocal(p.id, "partytype", e.target.value)}
                        style={{ padding: 2 }}
                      >
                        <option value="hawker">hawker</option>
                        <option value="wholesale">wholesale</option>
                        <option value="swd">swd</option>
                        <option value="npsr">npsr</option>
						<option value="management">management</option>
                      </select>
                    </td>
                    <td style={{ padding: 8 }}>{p.creditLimit}</td>
                    <td style={{ padding: 8 }}>{p.opening_balance}</td>
                    <td style={{ padding: 8 }}>
                      <select
                        value={p.payment_mode || "Cash"}
                        onChange={(e) => updateFieldLocal(p.id, "payment_mode", e.target.value)}
                        style={{ padding: 2 }}
                      >
                        <option value="Cash">Cash</option>
                        <option value="Credit">Credit</option>
                      </select>
                    </td>

                    <td style={{ padding: 8 }}>
                      <input
                        type="number"
                        value={p.routePotential || 0}
                        onChange={(e) => updateFieldLocal(p.id, "routePotential", e.target.value)}
                        style={{ width: "60px", padding: 4 }}
                      />
                    </td>
                    <td style={{ padding: 8 }}>
                      <input
                        type="number"
                        value={p.depositAmount || 0}
                        onChange={(e) => updateFieldLocal(p.id, "depositAmount", e.target.value)}
                        style={{ width: "80px", padding: 4 }}
                      />
                    </td>
                    <td style={{ padding: 8, whiteSpace: "nowrap" }}>
                      <button 
                        onClick={() => editPartyInForm(p)}
                        style={{ backgroundColor: "#ffc107", border: "none", padding: "4px 8px", borderRadius: 4, cursor: "pointer", marginRight: 5 }}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => savePartyChanges(p)}
                        style={{ backgroundColor: "#28a745", color: "white", border: "none", padding: "4px 8px", borderRadius: 4, cursor: "pointer", marginRight: 5 }}
                      >
                        Save
                      </button>
                      <button
                        style={{
                          backgroundColor: "#dc3545",
                          color: "white",
                          border: "none",
                          padding: "4px 8px",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                        onClick={() => deleteParty(p)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="16" style={{ textAlign: "center", padding: 20, color: "#999" }}>No parties found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default PartyManager;
